/**
 * Copyright (c) 2026, CYBRA Corp. All rights reserved.
 */
import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { KissOutboundGetContainerDetailsDto, KissOutboundState, KissOutboundStateInfo, KissService } from "app/services/kiss.service";

@Component({
    templateUrl: './kiss-outbound-validation.enter-info.component.html',
    styleUrls: ['./kiss-outbound-validation.enter-info.component.css']
  })
export class KissOutboundValidationEnterInfoComponent implements OnInit, AfterViewInit {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  portal: ServiceInstance;

  containerId: string = '';

  isUiDisabled: boolean = false;

  /** Constructor */
  constructor(
    private kissService: KissService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  /**
   * Initializes the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnInit(): void {
    this.route.data.subscribe(
      (data: {
        portal: ServiceInstance
      }) => {
        console.log(`Obtained Portal: '${data?.portal?.id}/${data?.portal?.name}'`);
        this.portal = data?.portal;
      });
  }

  /**
   * Initializes the component view.  
   * Called automatically by the Angular Component Lifecycle.
   * 
   * Used to auto-focus on the "Container ID" input field.  
   */
  ngAfterViewInit(): void {
    // Auto-focus on the "Carton ID" input (Not using the "[autofocus]" as that only works the 1st time the page is displayed, not after migrating back after clearing / processing an order)
    const element = document.getElementById("container-id-input");
    if (element != null && element != undefined) {
      element.focus();
    }
  }

  /**
   * Called when the "Get Info" Button is clicked in the UI.
   * Calls the Server to get the "Container Information" to validate against,
   * then forwards to the Dashboard.
   */
  onGetInfoClicked(): void {
    if (!this.isUiDisabled) {
      this.isUiDisabled = true;
      this.indefiniteProgressDialog.show("Processing", "Obtaining Container info...");
      this.getInfo();
    }
  }

  async getInfo(): Promise<void> {
    const dto = {
      container_id: this.containerId
    } as KissOutboundGetContainerDetailsDto;

    let state: KissOutboundStateInfo = null;
    try {
      state = await this.kissService.enterOutboundInfo(this.portal.id, dto).toPromise();
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.isUiDisabled = false;
      this.handleError(error);
      return;
    }

    this.isUiDisabled = false;
    this.indefiniteProgressDialog.hide();
    console.log(`Obtained State after Enter Info: ${JSON.stringify(state)}`);

    if (state?.state == KissOutboundState.AttemptBeingValidated) { 
      this.router.navigate(['kiss', 'outbound-validation', this.portal.id, 'dashboard'], {});
    }
  }

  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}